import React, { useEffect, useRef } from "react";
import { $Typography } from "us.common/components";
import "./ChatBubble.scss";

type ChatBubbleProps = {
  id: string;
  responseType: string;
  responseContent: string;
  links: string[];
  timestamp: Date;
  isUser?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  loadingStatus?: string;
  fullWidth?: boolean;
  attachments?: {
    type: "image" | "file";
    url: string;
    name: string;
    size?: string;
  }[];
};

/**
 * ChatBubble component displays a single chat message bubble, supporting user and AI messages,
 * attachments (images and files), links, and a timestamp.
 *
 * Features:
 * - Auto-scrolls into view when a new message appears or when images load.
 * - Renders images and files as attachments with appropriate UI.
 * - Shows a loading indicator for AI messages that are being generated.
 * - Displays links as clickable elements.
 * - Formats and displays the message timestamp.
 *
 * @component
 * @param {ChatBubbleProps} props - The properties for the ChatBubble component.
 * @param {string} props.id - Unique identifier for the message.
 * @param {string} props.responseContent - The main text content of the message.
 * @param {string[]} [props.links] - Optional array of links to display in the message.
 * @param {Date} props.timestamp - The timestamp of the message.
 * @param {boolean} [props.isUser=false] - Whether the message is from the user.
 * @param {boolean} [props.isLoading=false] - Whether the message is in a loading state (AI typing).
 * @param {boolean} [props.isError=false] - Whether the message is an error message.
 * @param {Attachment[]} [props.attachments=[]] - Optional array of attachments (images or files).
 *
 * @returns {JSX.Element} The rendered chat bubble.
 */
const ChatBubble: React.FC<ChatBubbleProps> = ({
  id,
  responseContent,
  links,
  timestamp,
  isUser = false,
  isLoading = false,
  isError = false,
  loadingStatus = "",
  fullWidth = false,
  attachments = [],
}) => {
  const messageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-scroll to new message when it appears
    if (messageRef.current) {
      messageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [id, responseContent]); // Trigger when message content changes

  // Helper function to render images
  const renderImages = () => {
    const images = attachments?.filter((att) => att.type === "image") || [];
    if (images.length === 0) return null;

    return (
      <div className="message-images">
        {images.map((image, index) => (
          <div key={index} className="image-container">
            <img
              src={image.url}
              alt={image.name}
              className="message-image"
              onLoad={(e) => {
                // Trigger scroll again after image loads
                if (messageRef.current) {
                  messageRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                  });
                }
              }}
            />
            <div className="image-caption">{image.name}</div>
          </div>
        ))}
      </div>
    );
  };

  // Helper function to render files
  const renderFiles = () => {
    const files = attachments?.filter((att) => att.type === "file") || [];
    if (files.length === 0) return null;

    return (
      <div className="message-files">
        {files.map((file, index) => (
          <div key={index} className="file-container">
            <div className="file-icon">📄</div>
            <div className="file-info">
              <div className="file-name">{file.name}</div>
              {file.size && <div className="file-size">{file.size}</div>}
            </div>
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="file-download"
            >
              ⬇️
            </a>
          </div>
        ))}
      </div>
    );
  };

  // Helper function to format and render text content
  const formatResponseContent = (content: string) => {
    if (!content) return null;

    // Split by line breaks (\r\n, \n, or \r)
    const lines = content.split(/\r\n|\n|\r/);

    return lines.map((line, lineIndex) => {
      if (!line.trim()) {
        // Empty line - add spacing
        return <br key={lineIndex} />;
      }

      // Process the line for markdown-style formatting
      let processedLine = line;
      const elements: (string | JSX.Element)[] = [];

      // Define formatting patterns in order of precedence
      const patterns = [
        // Links [text](url) - process first to avoid conflicts
        {
          regex: /\[([^\]]+)\]\(([^)]+)\)/g,
          render: (text: string, url: string, key: string) => (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="formatted-link"
            >
              {text}
            </a>
          ),
        },
        // Bold **text**
        {
          regex: /\*\*([^*]+)\*\*/g,
          render: (text: string, _: string, key: string) => (
            <strong key={key}>{text}</strong>
          ),
        },
        // Italic *text* (but not part of **)
        {
          regex: /(?<!\*)\*([^*]+)\*(?!\*)/g,
          render: (text: string, _: string, key: string) => (
            <em key={key}>{text}</em>
          ),
        },
        // URLs without markdown syntax
        {
          regex: /(https?:\/\/[^\s\]]+)/g,
          render: (url: string, _: string, key: string) => (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="formatted-link"
            >
              {url}
            </a>
          ),
        },
      ];

      // Create a single pass through the text to find all matches
      const allMatches: Array<{
        start: number;
        end: number;
        element: JSX.Element;
        priority: number;
      }> = [];

      patterns.forEach((pattern, patternIndex) => {
        const matches = Array.from(processedLine.matchAll(pattern.regex));

        matches.forEach((match) => {
          const start = match.index!;
          const end = start + match[0].length;
          const key = `${patternIndex}-${lineIndex}-${start}`;

          allMatches.push({
            start,
            end,
            element: pattern.render(match[1] || match[0], match[2] || "", key),
            priority: patternIndex,
          });
        });
      });

      // Sort matches by start position and priority
      allMatches.sort((a, b) => a.start - b.start || a.priority - b.priority);

      // Remove overlapping matches (keep higher priority ones)
      const validMatches = [];
      let lastEnd = 0;

      for (const match of allMatches) {
        if (match.start >= lastEnd) {
          validMatches.push(match);
          lastEnd = match.end;
        }
      }

      // Build the final elements array
      let currentIndex = 0;

      validMatches.forEach((match, matchIndex) => {
        // Add text before the match
        if (match.start > currentIndex) {
          const beforeText = processedLine.slice(currentIndex, match.start);
          if (beforeText) {
            elements.push(beforeText);
          }
        }

        // Add the formatted element
        elements.push(match.element);
        currentIndex = match.end;
      });

      // Add remaining text
      if (currentIndex < processedLine.length) {
        const remainingText = processedLine.slice(currentIndex);
        if (remainingText) {
          elements.push(remainingText);
        }
      }

      // If no matches found, use the original line
      if (elements.length === 0) {
        elements.push(processedLine);
      }

      return (
        <div
          key={lineIndex}
          className={`message-line ${
            line.includes(":") && !line.includes("http") ? "data-line" : ""
          }`}
        >
          {elements.map((element, elementIndex) =>
            typeof element === "string" ? (
              <span key={`text-${lineIndex}-${elementIndex}`}>{element}</span>
            ) : (
              element
            )
          )}
        </div>
      );
    });
  };

  if (isLoading) {
    return (
      <div ref={messageRef} className="message ai-message loading">
        <div className="message-wrapper">
          <div className="message-content">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
            {loadingStatus && (
              <div className="loading-status">
                <$Typography className="status-text">
                  {loadingStatus}
                </$Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={messageRef}
      className={`message ${isUser ? "user-message" : "ai-message"}${
        isError ? " error-message" : ""
      }`}
    >
      <div className="message-wrapper">
        <div className={`message-content${fullWidth ? " full-width" : ""}`}>
          {responseContent && (
            <div className="message-text">
              {formatResponseContent(responseContent)}
            </div>
          )}

          {/* Render images */}
          {renderImages()}

          {/* Render files */}
          {renderFiles()}

          {/* Render links */}
          {links && links.length > 0 && (
            <div className="message-links">
              {links.map((link, index) => (
                <a
                  key={index}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="message-link"
                >
                  🔗 {link}
                </a>
              ))}
            </div>
          )}
        </div>
        <$Typography className="message-time">
          {timestamp?.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </$Typography>
      </div>
    </div>
  );
};

export default ChatBubble;
